import { Navigate, Outlet, useLocation, Link } from "react-router-dom";
import { onlineManager } from "@tanstack/react-query";
import { useMe } from "../../lib/auth";

/**
 * Route guard for authenticated areas.
 *
 * Offline-tolerant on purpose. `useMe` is served from the persisted query cache
 * (lib/persist.ts), so on a cold launch with no signal `data.user` is already
 * there while the background refetch fails. Redirecting on `isError` — as this
 * did — would throw a signed-in user out to /login the moment they lost
 * connection, which is exactly what the packaged app exists to avoid. The only
 * thing that signs you out is the server actually saying so: `useMe` maps 401 to
 * `{ user: null }`, and that lands in the cache as a success.
 *
 * While `/api/me` is loading, shows a minimal centered violet glyph (no spinner —
 * keeps the editorial mood). On unauthed, redirects to /login?next=<currentPath>.
 */
export function RequireAuth() {
  const { data, isPending, isError } = useMe();
  const location = useLocation();

  /* OFFLINE WITH NOTHING CACHED is not the same as signed out, and must not be
   * shown as a login form. `/api/me` cannot answer, so `data` is undefined and
   * the check below would read that as "no user" and redirect — which is how a
   * cold offline launch used to land on /login. We do not know whether this
   * person is signed in; we know we cannot find out. Say that, and offer the
   * part of the app that works without an answer. */
  if (isError && !data && !onlineManager.isOnline()) {
    return (
      <div className="min-h-[60svh] flex flex-col items-center justify-center gap-4 px-6 text-center" aria-live="polite">
        <span className="font-deva text-[3rem] leading-none" style={{ color: "var(--color-violet)", opacity: 0.4 }} aria-hidden>ॐ</span>
        <p className="font-grotesk text-sm" style={{ color: "var(--color-ink-mute)" }}>
          You’re offline, so your account can’t be checked just now.<br />
          It will pick up where you left off once you have signal.
        </p>
        <Link to="/library" className="btn-ghost">Read the library offline</Link>
      </div>
    );
  }

  /* `isPending`, NOT `isLoading`. In react-query v5 `isLoading` is
   * `isPending && isFetching`, and while the persisted cache is being restored
   * the queries are PAUSED — so `isFetching` is false, `isLoading` reads false
   * while `data` is still undefined, and the check below concluded "no user"
   * and redirected. That is the flash of /login?next=… on a hard refresh:
   * correct a moment later, wrong for the moment that matters. `isPending`
   * covers both the restore and the first fetch, which is the actual question:
   * do we have an answer yet? */
  if (isPending) {
    return (
      <div className="min-h-[60svh] flex items-center justify-center" aria-live="polite">
        <span
          className="font-deva text-[3.5rem] tracking-[-0.05em]"
          style={{ color: "var(--color-violet)", opacity: 0.4 }}
          aria-label="Loading"
        >
          ॐ
        </span>
      </div>
    );
  }

  if (!data?.user) {
    const nextPath = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  return <Outlet />;
}
